import { buildSlice, fetchRecordingsOfUser, IRecording, RecordingState, Services } from '@24sens/ecg01-recordings';
import { ApiError, serializeError } from '@24sens/ecg01-rest-client';
import { selectorChannel } from '@24sens/utils';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { call, fork, put, takeEvery } from 'typed-redux-saga';

import * as globalSelectors from '../store/selectors';

export interface RecordingPaginationPage {
  recordings: IRecording[];
  nextCursor: string | null;
  previousCursor: string | null;
}

interface RecordingPaginationState {
  pagesByPatient: Record<number, RecordingPaginationPage>;
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
}

function extractCursor(url: string | null | undefined): string | null {
  if (!url) { return null; }
  try {
    return new URL(url, 'http://placeholder').searchParams.get('cursor');
  } catch {
    return null;
  }
}

export const fetchRecordingsPage = createAsyncThunk<
  { recordings: IRecording[]; nextCursor: string | null; previousCursor: string | null; userId: number },
  { userId: number; cursor?: string | null },
  { rejectValue: ApiError; extra: Services }
>(
  'recordingPagination/fetchPage',
  async ({ userId, cursor }, thunkApi) => {
    const response = await (thunkApi.extra as Services).patient.recordingList(userId, cursor ?? undefined);
    return {
      recordings: response.data.results as IRecording[],
      nextCursor: extractCursor(response.data.next),
      previousCursor: extractCursor(response.data.previous),
      userId,
    };
  },
  { serializeError },
);

export const recordingPaginationSlice = createSlice({
  name: 'recordingPagination',
  initialState: { pagesByPatient: {}, loading: 'idle' } as RecordingPaginationState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchRecordingsPage.pending, (state) => {
      state.loading = 'pending';
    });
    builder.addCase(fetchRecordingsPage.fulfilled, (state, action) => {
      state.loading = 'succeeded';
      const { recordings, nextCursor, previousCursor, userId } = action.payload;
      state.pagesByPatient[userId] = { recordings, nextCursor, previousCursor };
    });
    builder.addCase(fetchRecordingsPage.rejected, (state) => {
      state.loading = 'failed';
    });
  },
});

const slice = buildSlice((state: { recording: RecordingState }) => state.recording);
export default slice;
export const selectors = slice.selectors;

function* fetchRecordings(userId: number) {
  if (userId) {
    yield* put(yield* call(fetchRecordingsOfUser, userId));
  }
}

function* watchAuthState() {
  const chan = yield* selectorChannel(globalSelectors.auth.userId);
  while (true) {
    try {
      yield* takeEvery(chan, fetchRecordings);
    } catch (e) {
      console.error('Failed to fetch recordings for user', e);
    }
  }
}

export function* saga() {
  console.info('Saga: recording saga');
  yield* fork(watchAuthState);
}
